import React, { useEffect, useMemo, useState } from "react";
import MachineTablesV2 from "./MachineTablesV2";
import GroupList from "../GroupList";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BackIcon, SearchesCIcon } from "../../assets/Icons";
import PortalV2Select from "../common/PortalV2Select";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

const PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { label: "Latest", value: "ascTime" },
  { label: "Oldest", value: "descTime" },
  { label: "Name (A-Z)", value: "ascName" },
  { label: "Name (Z-A)", value: "descName" },
];

const MachineListV2 = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [machineList, setMachineList] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("ascTime");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("list");

  const fetchAllMachines = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/machine/all/${groupId}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setMachineList(data.data || []);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllMachines();
  }, [groupId]);

  const allTags = useMemo(() => {
    const map = new Map();
    machineList.forEach((machine) => {
      machine.tags?.forEach((tag) => {
        if (tag?.id) map.set(tag.id, tag.name);
      });
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [machineList]);

  const stats = useMemo(() => {
    const total = machineList.length;
    const active = machineList.filter((m) => m.isActive).length;
    const hidden = total - active;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = machineList.filter((m) => new Date(m.updatedAt).getTime() >= weekAgo).length;
    return { total, active, hidden, recent };
  }, [machineList]);

  const filteredList = useMemo(() => {
    return machineList.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
      const matchesVisibility =
        visibilityFilter === "all" ||
        (visibilityFilter === "active" && item.isActive) ||
        (visibilityFilter === "hidden" && !item.isActive);
      const matchesTag =
        tagFilter === "all" || item.tags?.some((tag) => String(tag.id) === String(tagFilter));
      return matchesSearch && matchesVisibility && matchesTag;
    });
  }, [machineList, search, visibilityFilter, tagFilter]);

  const sortedList = useMemo(() => {
    const list = [...filteredList];
    if (sortBy === "ascTime") {
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === "descTime") {
      list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (sortBy === "ascName") {
      list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "descName") {
      list.sort((a, b) => b.name.localeCompare(a.name));
    }
    return list;
  }, [filteredList, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, visibilityFilter, tagFilter, sortBy]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedList.slice(start, start + PAGE_SIZE);
  }, [sortedList, page]);

  const resetFilters = () => {
    setSearch("");
    setSortBy("ascTime");
    setVisibilityFilter("all");
    setTagFilter("all");
    setPage(1);
  };

  const tagOptions = useMemo(
    () => [
      { label: "All Tags", value: "all" },
      ...allTags.map((tag) => ({ label: tag.name, value: String(tag.id) })),
    ],
    [allTags],
  );

  const visibilityOptions = useMemo(
    () => [
      { label: "All Visibility", value: "all" },
      { label: "Active", value: "active" },
      { label: "Hidden", value: "hidden" },
    ],
    [],
  );

  const canAdd = userData?.user?.permissions?.includes("MACHINE.ADD");

  if (loading) {
    return (
      <div className="sites-v2 machines-v2">
        <Skeleton height={36} width={200} />
        <Skeleton height={20} width={360} className="mt-2" />
        <div className="sites-v2__stats mt-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={110} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sites-v2 machines-v2">
      <div className="machines-v2__tabs">
        <button type="button" className="machines-v2__back" onClick={() => navigate(-1)}>
          <BackIcon />
          Back
        </button>
        <button
          type="button"
          className={`machines-v2__tab ${activeTab === "list" ? "active" : ""}`}
          onClick={() => setActiveTab("list")}
        >
          Machine List
        </button>
        <button
          type="button"
          className={`machines-v2__tab ${activeTab === "groups" ? "active" : ""}`}
          onClick={() => setActiveTab("groups")}
        >
          Groups
        </button>
      </div>

      {activeTab === "groups" ? (
        <div className="machines-v2__groups">
          <GroupList setActiveTab={() => setActiveTab("list")} navigateLink="/machine-list/view" />
        </div>
      ) : (
        <>
          <div className="sites-v2__head">
            <h1 className="sites-v2__title">Machines</h1>
            <p className="sites-v2__subtitle">Manage and monitor all configured machines</p>
          </div>

          <div className="sites-v2__stats">
            <article className="sites-v2__stat-card">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--red">
                <PrecisionManufacturingOutlinedIcon />
              </div>
              <div>
                <strong>{stats.total}</strong>
                <span>Total Machines</span>
                <small>All configured machines</small>
              </div>
            </article>
            <article className="sites-v2__stat-card">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
                <CheckCircleOutlineIcon />
              </div>
              <div>
                <strong>{stats.active}</strong>
                <span>Active Machines</span>
                <small>Visible to users</small>
              </div>
            </article>
            <article className="sites-v2__stat-card">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
                <VisibilityOffOutlinedIcon />
              </div>
              <div>
                <strong>{stats.hidden}</strong>
                <span>Hidden Machines</span>
                <small>Not visible to users</small>
              </div>
            </article>
            <article className="sites-v2__stat-card">
              <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
                <ScheduleOutlinedIcon />
              </div>
              <div>
                <strong>{stats.recent}</strong>
                <span>Recently Updated</span>
                <small>In the last 7 days</small>
              </div>
            </article>
          </div>

          {machineList.length === 0 ? (
            <div className="sites-v2__empty">
              <p>No Machine Found!</p>
              {canAdd && (
                <button type="button" className="sites-v2__add-btn" onClick={() => navigate(`/machine-list/create`)}>
                  + Add New Machine
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="sites-v2__toolbar">
                <label className="sites-v2__search">
                  <SearchesCIcon />
                  <input
                    type="text"
                    placeholder="Search machines..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
                <PortalV2Select
                  label="Latest"
                  value={sortBy}
                  options={SORT_OPTIONS}
                  onChange={setSortBy}
                />
                <PortalV2Select
                  label="Tags"
                  value={tagFilter}
                  options={tagOptions}
                  onChange={setTagFilter}
                />
                <PortalV2Select
                  label="Visibility"
                  value={visibilityFilter}
                  options={visibilityOptions}
                  onChange={setVisibilityFilter}
                />
                <button type="button" className="sites-v2__reset-btn" onClick={resetFilters}>
                  ↻ Reset
                </button>
                {canAdd && (
                  <button type="button" className="sites-v2__add-btn" onClick={() => navigate(`/machine-list/create`)}>
                    + Add New Machine
                  </button>
                )}
              </div>

              <MachineTablesV2
                data={paginatedList}
                fetchAllMachines={fetchAllMachines}
                page={page}
                pageSize={PAGE_SIZE}
                totalCount={sortedList.length}
                onPageChange={setPage}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default MachineListV2;
